import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';

import { deleteCustomFormResponse } from 'api/CustomForms';

import { useClinicalNoteContext } from 'lib/context/ClinicalNoteContext/ClinicalNoteContext';
import { iaRa } from 'lib/helpers/utility';

import NarrativeForm from 'components/practice/charts/ClinicalNote/shared/NarrativeForm';
import { showAlert } from 'components/shared/Alert/Alert';
import Checkbox from 'components/shared/Checkbox/Checkbox';
import DateInputPicker from 'components/shared/DateInputPicker/DateInputPicker';
import { SocialHistory as initialValue } from 'components/shared/Forms/Custom/CustomFormInitialStates';
import {
  scrollIntoNarrativeView,
  submitForm
} from 'components/shared/Forms/Custom/lib/customFormsHelper';
import Input from 'components/shared/Input/Input';
import RadioButton from 'components/shared/RadioButton/RadioButton';
import { intelligenceAiState, permissions, userState } from 'components/state';

import Container from '../components/Container';
import { deleteFormOverviewAndHpNote } from '../lib/deleteCustomForms';
import { createSocialHistoryNarrative } from '../lib/socialHistoryNarrativeHelper';

import './SocialHistory.scss';

const ClinicalSocialHistory = ({
  forwardedRef,
  defaultValue,
  setCurrentFormData,
  hpNoteOverviewRef,
  // sectionRef,
  customFormsParams,
  formId = null,
  showNarrative = false,
  fromClinicalNote = false,
  onChange,
  setItemModal = () => {},
  options = [],
  diffDosView = false
}) => {
  const [socialHistory, setSocialHistory] = useState(initialValue);
  const [prevSocialHistory, setPrevSocialHistory] = useState(initialValue);
  const { practice_id } = useRecoilValue(permissions);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { cnDisplaySettings, setOverviewData, setCurrentHpOverviewData } =
    useClinicalNoteContext() || {};
  const [syncNarrative, setSyncNarrative] = useState(true);
  const isAdvancedForm =
    cnDisplaySettings && showNarrative
      ? cnDisplaySettings?.sections?.hp?.socialHistory?.advancedForm
      : true;
  const user = useRecoilValue(userState);
  const intelligenceAi = useRecoilValue(intelligenceAiState);
  const textAreaRef = useRef();

  // useImperativeHandle(sectionRef, () => ({
  //   formData: { advancedHP: currentForm }
  // }));

  useEffect(() => {
    if (defaultValue) {
      if (typeof defaultValue === 'string') {
        setSocialHistory(JSON.parse(defaultValue));
      } else {
        setSocialHistory(defaultValue);
      }
    } else {
      setSocialHistory(initialValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    notifyChange(socialHistory);
  }, [socialHistory]);

  useEffect(() => {
    if (
      socialHistory?.narrative !== intelligenceAi.response &&
      intelligenceAi?.path?.includes?.('socialHistory') &&
      intelligenceAi.response !== ''
    ) {
      setSocialHistory((ps) => ({
        ...ps,
        narrative: intelligenceAi.response ?? ps?.narrative
      }));
    }
  }, [intelligenceAi]);

  const notifyChange = (updatedValue) => {
    if (onChange) {
      onChange({ target: { value: updatedValue, name: 'SocialHistory', type: 'custom' } });
    }
  };

  const syncSocialHistoryAndClinicalNotes = (updatedObject) => {
    if (syncNarrative) {
      generateNarrative({ socialHistoryObject: updatedObject });
    } else {
      setSocialHistory(updatedObject);
      setCurrentFormData && setCurrentFormData(updatedObject);
    }
  };

  const handleNumberOfChildren = (nr) => {
    const newArray = [];
    const obj = { age: null };
    for (let i = 0; i < nr; i++) {
      newArray.push(obj);
    }
    handleOnChange({
      children: {
        ...socialHistory?.children,
        numberOfChildren: nr,
        ages: newArray
      }
    });
    setCurrentFormData &&
      setCurrentFormData((prevState) => ({
        ...prevState,
        children: {
          ...socialHistory?.children,
          numberOfChildren: nr,
          ages: newArray
        }
      }));
  };

  const updateChildAge = (e, idx) => {
    const updatedArr = [...(socialHistory?.children?.ages ?? [])];
    updatedArr[idx] = e;
    handleOnChange({
      children: {
        ...socialHistory?.children,
        ages: updatedArr
      }
    });
    setCurrentFormData &&
      setCurrentFormData((prevState) => ({
        ...prevState,
        children: {
          ...socialHistory?.children,
          ages: updatedArr
        }
      }));
  };

  const doYou = 'Do you';
  const doesThePatient = 'Does the patient';

  const handleNoDetails = () => {
    const updatedObject = {
      ...socialHistory,
      noDetails: !socialHistory?.noDetails
    };

    syncSocialHistoryAndClinicalNotes(updatedObject);
  };

  const handleOnChange = (object) => {
    const updatedObject = {
      ...socialHistory,
      ...object
    };
    syncSocialHistoryAndClinicalNotes(updatedObject);
  };

  const handleNarrative = ({
    socialHistoryObject = socialHistory,
    event = '',
    scrollable = false,
    sync = true
  }) => {
    if (typeof event === 'object' && event?.target?.value) {
      event = event?.target?.value;
    }

    setSyncNarrative(sync);

    const updatedObject = { ...socialHistoryObject, narrative: event };

    setSocialHistory(updatedObject);
    setCurrentFormData && setCurrentFormData(updatedObject);

    scrollable && scrollIntoNarrativeView({ ref: textAreaRef });
  };

  const narrativeOptions = [
    {
      group: 'actions',
      icon: 'update-mono',
      title: 'Update & Sync',
      onClick: () => generateNarrative({ scrollable: true })
    }
  ];

  const generateNarrative = ({ socialHistoryObject = socialHistory, scrollable = false } = {}) => {
    const formattedNarrative = createSocialHistoryNarrative(socialHistoryObject);

    handleNarrative({
      socialHistoryObject,
      event: formattedNarrative,
      scrollable
    });
  };

  const mutateDeleteMedicalHistory = useMutation({
    mutationFn: () =>
      deleteCustomFormResponse(navigate, {
        customFormId: formId
      }),
    onSuccess: ({ code, error }) => {
      if (code !== 0) {
        showAlert({
          title: 'Social History',
          message: error,
          color: 'warning'
        });
        return;
      }
      setSocialHistory(initialValue);
      formId &&
        fromClinicalNote &&
        deleteFormOverviewAndHpNote({
          setOverviewData,
          setHpNoteData: setCurrentHpOverviewData,
          formId,
          type: 'socialHistory'
        });
      showAlert({
        title: `Social History deleted successfully`
      });

      queryClient.invalidateQueries(['customFormResponse']);
      setItemModal();
    }
  });

  return (
    <div className="flex flex-row">
      <div className="SocialHistory w-full">
        <input type="hidden" value={JSON.stringify(socialHistory)} ref={forwardedRef} />

        <p className="mb-4 text-center text-lg font-500 text-primary-900">Social History</p>

        {isAdvancedForm && (
          <Checkbox
            label="No notable social history"
            data-qa="no-notable-social-history"
            name="noDetails"
            isChecked={socialHistory?.noDetails}
            onChange={handleNoDetails}
            disabled={diffDosView}
          />
        )}

        {!socialHistory?.noDetails && isAdvancedForm && (
          <Container
            onDelete={() => {
              if (!formId) {
                showAlert({
                  title: 'Social History',
                  message: 'Please create the form before deleting one.',
                  color: 'warning'
                });
                return;
              }
              mutateDeleteMedicalHistory.mutate();
            }}
            diffDosView={diffDosView}
            newDesign={false}>
            <div className="SocialHistory__grid">
              <div className="SocialHistory__row">
                <p className="SocialHistory__row__title">
                  {practice_id ? doesThePatient : doYou} smoke cigarettes?
                </p>
                <div className="SocialHistory__row__content flex">
                  <RadioButton
                    wrapperClassName="w-max"
                    data-qa="never"
                    label="Never"
                    isSelected={socialHistory?.cigarettes?.never}
                    onChange={() =>
                      handleOnChange({
                        cigarettes: {
                          ...socialHistory?.cigarettes,
                          never: !socialHistory?.cigarettes?.never,
                          yes: false,
                          packsDay: '',
                          quit: false,
                          dateQuit: null,
                          yearsSmoked: ''
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                  <RadioButton
                    wrapperClassName="w-max"
                    data-qa="yes"
                    label="Yes"
                    isSelected={socialHistory?.cigarettes?.yes}
                    onChange={() => {
                      handleOnChange({
                        cigarettes: {
                          ...socialHistory?.cigarettes,
                          yes: !socialHistory?.cigarettes?.yes,
                          never: false,
                          quit: false,
                          dateQuit: null,
                          yearsSmoked: ''
                        }
                      });
                    }}
                    disabled={diffDosView}
                  />
                  <RadioButton
                    wrapperClassName="w-max"
                    data-qa="quit"
                    label="Quit"
                    isSelected={socialHistory?.cigarettes?.quit}
                    onChange={() =>
                      handleOnChange({
                        cigarettes: {
                          ...socialHistory?.cigarettes,
                          quit: !socialHistory?.cigarettes?.quit,
                          dateQuit: new Date(),
                          never: false,
                          yes: false
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                </div>
                {socialHistory?.cigarettes?.yes && (
                  <div className="flex items-center gap-1">
                    <Input
                      data-qa="packs-per-day"
                      label="Packs per day"
                      type="number"
                      pattern="\d*"
                      min="0"
                      className="w-full text-right"
                      value={socialHistory?.cigarettes.packsDay}
                      onChange={(e) =>
                        handleOnChange({
                          cigarettes: {
                            ...socialHistory?.cigarettes,
                            packsDay: e.target.value
                          }
                        })
                      }
                      disabled={diffDosView}
                    />
                  </div>
                )}

                {socialHistory?.cigarettes?.quit && (
                  <div className="grid grid-cols-[repeat(auto-fit,minmax(264px,1fr))] gap-4">
                    <Input
                      data-qa="years-smoked"
                      label="Years smoked"
                      placeholder="Type how many years smoked"
                      transcribing
                      value={socialHistory?.cigarettes.yearsSmoked}
                      onChange={(e) =>
                        handleOnChange({
                          cigarettes: {
                            ...socialHistory?.cigarettes,
                            yearsSmoked: e.target.value
                          }
                        })
                      }
                      disabled={diffDosView}
                    />
                    <DateInputPicker
                      label="Quit date"
                      isDateOnly
                      value={socialHistory?.cigarettes.dateQuit}
                      onChange={(date) =>
                        handleOnChange({
                          cigarettes: { ...socialHistory?.cigarettes, dateQuit: date }
                        })
                      }
                      disabled={diffDosView}
                    />
                  </div>
                )}
              </div>
              <div className="SocialHistory__row">
                <p className="SocialHistory__row__title">
                  {practice_id ? doesThePatient : doYou} vape (e-cigarettes)?
                </p>
                <div className="SocialHistory__row__content flex">
                  <RadioButton
                    wrapperClassName="w-max"
                    data-qa="yes"
                    label="Yes"
                    isSelected={socialHistory?.vape?.yes}
                    onChange={() =>
                      handleOnChange({
                        vape: {
                          ...socialHistory?.vape,
                          yes: !socialHistory?.vape?.yes,
                          no: false
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                  <RadioButton
                    wrapperClassName="w-max"
                    data-qa="no"
                    label="No"
                    isSelected={socialHistory?.vape?.no}
                    onChange={() =>
                      handleOnChange({
                        vape: {
                          ...socialHistory?.vape,
                          no: !socialHistory?.vape?.no,
                          yes: false
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                </div>
              </div>
              <div className="SocialHistory__row">
                <p className="SocialHistory__row__title">
                  {practice_id ? doesThePatient : doYou} drink alcohol?
                </p>
                <div className="SocialHistory__row__content flex">
                  <RadioButton
                    wrapperClassName="w-max"
                    data-qa="yes"
                    label="Yes"
                    isSelected={socialHistory?.alcohol?.yes}
                    onChange={() =>
                      handleOnChange({
                        alcohol: {
                          ...socialHistory?.alcohol,
                          yes: !socialHistory?.alcohol?.yes,
                          never: false
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                  <RadioButton
                    wrapperClassName="w-max"
                    data-qa="no"
                    label="No"
                    isSelected={socialHistory?.alcohol?.never}
                    onChange={() =>
                      handleOnChange({
                        alcohol: {
                          ...socialHistory?.alcohol,
                          never: !socialHistory?.alcohol?.never,
                          yes: false,
                          drinksWeek: ''
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                </div>

                {socialHistory?.alcohol?.yes && (
                  <div className="flex items-center gap-1">
                    <Input
                      data-qa="drinks"
                      className="w-full"
                      label="Drinks"
                      placeholder="Enter many drinks per week"
                      transcribing
                      value={socialHistory?.alcohol.drinksWeek}
                      onChange={(e) =>
                        handleOnChange({
                          alcohol: {
                            ...socialHistory?.alcohol,
                            drinksWeek: e.target.value
                          }
                        })
                      }
                      disabled={diffDosView}
                    />
                  </div>
                )}
              </div>
              <div className="SocialHistory__row">
                <p className="SocialHistory__row__title">
                  {practice_id ? doesThePatient : doYou} use recreational drugs?
                </p>
                <div className="SocialHistory__row__content grid-cols-[repeat(auto-fit,minmax(96px,1fr))]">
                  <Checkbox
                    data-qa="never"
                    label="Never"
                    isChecked={socialHistory?.drugs?.never}
                    onChange={() =>
                      handleOnChange({
                        drugs: {
                          ...socialHistory?.drugs,
                          never: !socialHistory?.drugs?.never,
                          rarely: false,
                          timesMonth: '',
                          marijuana: false,
                          cocaine: false,
                          opioid: false,
                          other: false,
                          otherText: ''
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                  <div className="grid gap-y-4">
                    <Checkbox
                      data-qa="rarely"
                      label="Rarely"
                      isChecked={socialHistory?.drugs?.rarely}
                      onChange={() =>
                        handleOnChange({
                          drugs: {
                            ...socialHistory?.drugs,
                            rarely: !socialHistory?.drugs?.rarely,
                            never: false
                          }
                        })
                      }
                      disabled={diffDosView}
                    />
                    {socialHistory?.drugs?.rarely && (
                      <Input
                        data-qa="amount"
                        label="Amount"
                        transcribing
                        placeholder="Enter how many times per month"
                        className="max-w-[280px] lg:max-w-none"
                        value={socialHistory?.drugs.timesMonth}
                        onChange={(e) =>
                          handleOnChange({
                            drugs: {
                              ...socialHistory?.drugs,
                              timesMonth: e.target.value,
                              never: false
                            }
                          })
                        }
                        disabled={diffDosView}
                      />
                    )}
                  </div>

                  <Checkbox
                    data-qa="marijuana"
                    label="Marijuana"
                    isChecked={socialHistory?.drugs?.marijuana}
                    onChange={() =>
                      handleOnChange({
                        drugs: {
                          ...socialHistory?.drugs,
                          marijuana: !socialHistory?.drugs?.marijuana,
                          never: false
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                  <Checkbox
                    data-qa="cocaine"
                    label="Cocaine"
                    isChecked={socialHistory?.drugs?.cocaine}
                    onChange={() =>
                      handleOnChange({
                        drugs: {
                          ...socialHistory?.drugs,
                          cocaine: !socialHistory?.drugs?.cocaine,
                          never: false
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                  <Checkbox
                    data-qa="opioids"
                    label="Opioids"
                    isChecked={socialHistory?.drugs?.opioid}
                    onChange={() =>
                      handleOnChange({
                        drugs: {
                          ...socialHistory?.drugs,
                          opioid: !socialHistory?.drugs?.opioid,
                          never: false
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                  <Checkbox
                    data-qa="other"
                    label="Other"
                    isChecked={socialHistory?.drugs?.other}
                    onChange={() =>
                      handleOnChange({
                        drugs: {
                          ...socialHistory?.drugs,
                          other: !socialHistory?.drugs?.other,
                          never: false
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                  {socialHistory?.drugs?.other && (
                    <Input
                      className="col-span-full w-full"
                      data-qa="enter-other-drugs"
                      label="Other"
                      placeholder="Enter other drugs"
                      value={socialHistory?.drugs?.otherText}
                      onChange={(e) =>
                        handleOnChange({
                          drugs: {
                            ...socialHistory?.drugs,
                            otherText: e.target.value
                          }
                        })
                      }
                      disabled={diffDosView}
                    />
                  )}
                </div>
              </div>
              <div className="SocialHistory__row">
                <p className="SocialHistory__row__title">
                  {practice_id ? "What's the patient's" : "What's your"} highest level of education
                  completed?
                </p>
                <div className="SocialHistory__row__content grid-cols-[repeat(auto-fit,minmax(118px,1fr))]">
                  <RadioButton
                    wrapperClassName="w-max"
                    label="High school"
                    isSelected={socialHistory?.education?.highSchool}
                    onChange={() =>
                      handleOnChange({
                        education: {
                          ...socialHistory?.education,
                          highSchool: !socialHistory?.education?.highSchool,
                          tradeSchool: false,
                          college: false,
                          postGraduateDegree: false
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                  <RadioButton
                    wrapperClassName="w-max"
                    data-qa="trade-school"
                    label="Trade school"
                    isSelected={socialHistory?.education?.tradeSchool}
                    onChange={() =>
                      handleOnChange({
                        education: {
                          ...socialHistory?.education,
                          tradeSchool: !socialHistory?.education?.tradeSchool,
                          highSchool: false,
                          college: false,
                          postGraduateDegree: false
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                  <RadioButton
                    wrapperClassName="w-max"
                    data-qa="college"
                    label="College"
                    isSelected={socialHistory?.education?.college}
                    onChange={() =>
                      handleOnChange({
                        education: {
                          ...socialHistory?.education,
                          college: !socialHistory?.education?.college,
                          highSchool: false,
                          tradeSchool: false,
                          postGraduateDegree: false
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                  <RadioButton
                    wrapperClassName="w-max"
                    label="Post-graduate degree(s)"
                    className="col-span-full"
                    isSelected={socialHistory?.education?.postGraduateDegree}
                    onChange={() =>
                      handleOnChange({
                        education: {
                          ...socialHistory?.education,
                          postGraduateDegree: !socialHistory?.education?.postGraduateDegree,
                          highSchool: false,
                          tradeSchool: false,
                          college: false
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                </div>
              </div>
              <div className="SocialHistory__row">
                <p className="SocialHistory__row__title">
                  {practice_id ? 'Is the patient' : 'Are you'} employed?
                </p>
                <div className="SocialHistory__row__content flex">
                  <RadioButton
                    wrapperClassName="w-max"
                    data-qa="yes"
                    label="Yes"
                    isSelected={socialHistory?.employed?.yes}
                    onChange={() =>
                      handleOnChange({
                        employed: {
                          ...socialHistory?.employed,
                          yes: !socialHistory?.employed?.yes,
                          no: false,
                          retired: false
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                  <RadioButton
                    wrapperClassName="w-max"
                    data-qa="no"
                    label="No"
                    isSelected={socialHistory?.employed?.no}
                    onChange={() =>
                      handleOnChange({
                        employed: {
                          ...socialHistory?.employed,
                          no: !socialHistory?.employed?.no,
                          retired: false,
                          yes: false,
                          typeOfWork: ''
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                  <RadioButton
                    wrapperClassName="w-max"
                    data-qa="retired"
                    label="Retired"
                    isSelected={socialHistory?.employed?.retired}
                    onChange={() =>
                      handleOnChange({
                        employed: {
                          ...socialHistory?.employed,
                          retired: !socialHistory?.employed?.retired,
                          no: false,
                          yes: false,
                          typeOfWork: ''
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                </div>
                {socialHistory?.employed?.yes && (
                  <Input
                    data-qa="type-of-work"
                    className="max-w-[280px] lg:max-w-none"
                    label="Type of work"
                    placeholder="Enter type of work"
                    transcribing
                    value={socialHistory?.employed.typeOfWork}
                    onChange={(e) =>
                      handleOnChange({
                        employed: {
                          ...socialHistory?.employed,
                          typeOfWork: e.target.value
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                )}
              </div>
              <div className="SocialHistory__row">
                <p className="SocialHistory__row__title">
                  {practice_id ? doesThePatient : doYou} exercise?
                </p>
                <div className="SocialHistory__row__content flex">
                  <RadioButton
                    wrapperClassName="w-max"
                    data-qa="yes"
                    label="Yes"
                    isSelected={socialHistory?.exercise?.yes}
                    onChange={() =>
                      handleOnChange({
                        exercise: {
                          ...socialHistory?.exercise,
                          yes: !socialHistory?.exercise?.yes,
                          no: false
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                  <RadioButton
                    wrapperClassName="w-max"
                    data-qa="no"
                    label="No"
                    isSelected={socialHistory?.exercise?.no}
                    onChange={() =>
                      handleOnChange({
                        exercise: {
                          ...socialHistory?.exercise,
                          no: !socialHistory?.exercise?.no,
                          yes: false,
                          type: '',
                          howOften: '',
                          howLongActivity: ''
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                </div>
                {socialHistory?.exercise?.yes && (
                  <div className="flex flex-col gap-4">
                    <Input
                      data-qa="type-of-exercise"
                      label="Type of exercise"
                      placeholer="Enter type of exercise"
                      transcribing
                      value={socialHistory?.exercise.type}
                      onChange={(e) =>
                        handleOnChange({
                          exercise: {
                            ...socialHistory?.exercise,
                            type: e.target.value
                          }
                        })
                      }
                      disabled={diffDosView}
                    />
                    <Input
                      data-qa="how-often"
                      label="How often"
                      placeholer="Enter how often patient exercises"
                      transcribing
                      value={socialHistory?.exercise.howOften}
                      onChange={(e) =>
                        handleOnChange({
                          exercise: {
                            ...socialHistory?.exercise,
                            howOften: e.target.value
                          }
                        })
                      }
                      disabled={diffDosView}
                    />
                    <Input
                      data-qa="activity"
                      label="Activity"
                      placeholer="Enter how long per activity"
                      transcribing
                      value={socialHistory?.exercise.howLongActivity}
                      onChange={(e) =>
                        handleOnChange({
                          exercise: {
                            ...socialHistory?.exercise,
                            howLongActivity: e.target.value
                          }
                        })
                      }
                      disabled={diffDosView}
                    />
                  </div>
                )}
              </div>
              <div className="SocialHistory__row">
                <p className="SocialHistory__row__title">
                  {practice_id ? "What's the patient's" : "What's your"} marital status?
                </p>
                <div className="SocialHistory__row__content grid-cols-[repeat(auto-fit,minmax(110px,1fr))]">
                  <RadioButton
                    wrapperClassName="w-max"
                    data-qa="married"
                    label="Married"
                    isSelected={socialHistory?.martialStatus?.married}
                    onChange={() =>
                      handleOnChange({
                        martialStatus: {
                          ...socialHistory?.martialStatus,
                          married: !socialHistory?.martialStatus?.married,
                          partnership: false,
                          divorced: false,
                          separated: false,
                          single: false,
                          widow: false
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                  <RadioButton
                    wrapperClassName="w-max"
                    data-qa="partnership"
                    label="Partnership"
                    isSelected={socialHistory?.martialStatus?.partnership}
                    onChange={() =>
                      handleOnChange({
                        martialStatus: {
                          ...socialHistory?.martialStatus,
                          partnership: !socialHistory?.martialStatus?.partnership,
                          married: false,
                          divorced: false,
                          separated: false,
                          single: false,
                          widow: false
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                  <RadioButton
                    wrapperClassName="w-max"
                    data-qa="divorced"
                    label="Divorced"
                    isSelected={socialHistory?.martialStatus?.divorced}
                    onChange={() =>
                      handleOnChange({
                        martialStatus: {
                          ...socialHistory?.martialStatus,
                          divorced: !socialHistory?.martialStatus?.divorced,
                          married: false,
                          partnership: false,
                          separated: false,
                          single: false,
                          widow: false
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                  <RadioButton
                    wrapperClassName="w-max"
                    data-qa="separated"
                    label="Separated"
                    isSelected={socialHistory?.martialStatus?.separated}
                    onChange={() =>
                      handleOnChange({
                        martialStatus: {
                          ...socialHistory?.martialStatus,
                          separated: !socialHistory?.martialStatus?.separated,
                          married: false,
                          partnership: false,
                          divorced: false,
                          single: false,
                          widow: false
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                  <RadioButton
                    wrapperClassName="w-max"
                    data-qa="single"
                    label="Single"
                    isSelected={socialHistory?.martialStatus?.single}
                    onChange={() =>
                      handleOnChange({
                        martialStatus: {
                          ...socialHistory?.martialStatus,
                          single: !socialHistory?.martialStatus?.single,
                          married: false,
                          partnership: false,
                          separated: false,
                          divorced: false,
                          widow: false
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                  <RadioButton
                    wrapperClassName="w-max"
                    data-qa="widow-er"
                    label="Widow/er"
                    isSelected={socialHistory?.martialStatus?.widow}
                    onChange={() =>
                      handleOnChange({
                        martialStatus: {
                          ...socialHistory?.martialStatus,
                          widow: !socialHistory?.martialStatus?.widow,
                          married: false,
                          partnership: false,
                          divorced: false,
                          separated: false,
                          single: false
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                </div>
              </div>
              <div className="SocialHistory__row">
                <p className="SocialHistory__row__title">
                  {practice_id ? 'Is the patient' : 'Are you'} sexually active?
                </p>
                <div className="grid gap-y-6">
                  <div className="SocialHistory__row__content flex">
                    <RadioButton
                      wrapperClassName="w-max"
                      data-qa="yes"
                      label="Yes"
                      isSelected={socialHistory?.sexualActivity?.yes}
                      onChange={() =>
                        handleOnChange({
                          sexualActivity: {
                            ...socialHistory?.sexualActivity,
                            yes: !socialHistory?.sexualActivity?.yes,
                            no: false
                          }
                        })
                      }
                      disabled={diffDosView}
                    />
                    <RadioButton
                      wrapperClassName="w-max"
                      data-qa="no"
                      label="No"
                      isSelected={socialHistory?.sexualActivity?.no}
                      onChange={() =>
                        handleOnChange({
                          sexualActivity: {
                            ...socialHistory?.sexualActivity,
                            no: !socialHistory?.sexualActivity?.no,
                            yes: false,
                            numberOfSexualPartners: '',
                            men: false,
                            women: false,
                            both: false
                          }
                        })
                      }
                      disabled={diffDosView}
                    />
                  </div>

                  {socialHistory?.sexualActivity?.yes && (
                    <div className="grid gap-y-6">
                      <Input
                        data-qa="partners"
                        className="max-w-[280px] lg:max-w-none"
                        label="Partners"
                        placeholder="Enter number of sexual partners"
                        transcribing
                        value={socialHistory?.sexualActivity?.numberOfSexualPartners}
                        onChange={(e) =>
                          handleOnChange({
                            sexualActivity: {
                              ...socialHistory?.sexualActivity,
                              numberOfSexualPartners: e.target.value
                            }
                          })
                        }
                        disabled={diffDosView}
                      />
                      <div className="flex gap-x-6">
                        <RadioButton
                          wrapperClassName="w-max"
                          data-qa="men"
                          label="Men"
                          isSelected={socialHistory?.sexualActivity?.men}
                          onChange={() => {
                            handleOnChange({
                              sexualActivity: {
                                ...socialHistory?.sexualActivity,
                                men: !socialHistory?.sexualActivity?.men,
                                both: false,
                                women: false
                              }
                            });
                          }}
                          disabled={diffDosView}
                        />
                        <RadioButton
                          wrapperClassName="w-max"
                          data-qa="women"
                          label="Women"
                          isSelected={socialHistory?.sexualActivity?.women}
                          onChange={() =>
                            handleOnChange({
                              sexualActivity: {
                                ...socialHistory?.sexualActivity,
                                women: !socialHistory?.sexualActivity?.women,
                                both: false,
                                men: false
                              }
                            })
                          }
                          disabled={diffDosView}
                        />
                        <RadioButton
                          wrapperClassName="w-max"
                          data-qa="both"
                          label="Both"
                          isSelected={socialHistory?.sexualActivity?.both}
                          onChange={() =>
                            handleOnChange({
                              sexualActivity: {
                                ...socialHistory?.sexualActivity,
                                both: !socialHistory?.sexualActivity?.both,
                                men: false,
                                women: false
                              }
                            })
                          }
                          disabled={diffDosView}
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col gap-4">
                    <span className="font-500 text-neutral-800">Contraception?</span>
                    <div className="flex gap-x-6">
                      <RadioButton
                        wrapperClassName="w-max"
                        data-qa="yes"
                        label="Yes"
                        isSelected={socialHistory?.sexualActivity?.contracteption}
                        onChange={() =>
                          handleOnChange({
                            sexualActivity: {
                              ...socialHistory?.sexualActivity,
                              contracteption: !socialHistory?.sexualActivity?.contracteption,
                              noContracteption: false
                            }
                          })
                        }
                        disabled={diffDosView}
                      />
                      <RadioButton
                        wrapperClassName="w-max"
                        data-qa="no"
                        label="No"
                        isSelected={socialHistory?.sexualActivity?.noContracteption}
                        onChange={() =>
                          handleOnChange({
                            sexualActivity: {
                              ...socialHistory?.sexualActivity,
                              noContracteption: !socialHistory?.sexualActivity?.noContracteption,
                              contracteption: false,
                              methodContraception: ''
                            }
                          })
                        }
                        disabled={diffDosView}
                      />
                    </div>
                  </div>
                  {socialHistory?.sexualActivity?.contracteption && (
                    <Input
                      data-qa="contraception-method"
                      className="max-w-[280px] lg:max-w-none"
                      label="Contraception method"
                      placeholder="Enter contraception method"
                      transcribing
                      value={socialHistory?.sexualActivity?.methodContraception}
                      onChange={(e) =>
                        handleOnChange({
                          sexualActivity: {
                            ...socialHistory?.sexualActivity,
                            methodContraception: e.target.value
                          }
                        })
                      }
                      disabled={diffDosView}
                    />
                  )}
                </div>
              </div>

              <div className="SocialHistory__row  !border-0">
                <p className="SocialHistory__row__title">
                  {practice_id ? doesThePatient : doYou} have children?
                </p>
                <div className="SocialHistory__row__content flex ">
                  <RadioButton
                    wrapperClassName="w-max"
                    data-qa="yes"
                    label="Yes"
                    isSelected={socialHistory?.children?.yes}
                    onChange={() =>
                      handleOnChange({
                        children: {
                          ...socialHistory?.children,
                          yes: !socialHistory?.children?.yes,
                          no: false
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                  <RadioButton
                    wrapperClassName="w-max"
                    data-qa="no"
                    label="No"
                    isSelected={socialHistory?.children?.no}
                    onChange={() =>
                      handleOnChange({
                        children: {
                          ...socialHistory?.children,
                          no: !socialHistory?.children?.no,
                          yes: false,
                          numberOfChildren: '',
                          ages: []
                        }
                      })
                    }
                    disabled={diffDosView}
                  />
                </div>

                {socialHistory?.children?.yes && (
                  <>
                    <Input
                      data-qa="enter-number"
                      className="max-w-[280px] lg:max-w-none"
                      label="Number of children"
                      placeholder="Enter number of children"
                      min="1"
                      type="number"
                      pattern="\d*"
                      value={socialHistory?.children.numberOfChildren}
                      onChange={(e) => handleNumberOfChildren(e.target.value)}
                      disabled={diffDosView}
                    />
                    <div className="grid grid-cols-[repeat(auto-fit,minmax(264px,1fr))] gap-4">
                      {socialHistory?.children.ages?.map((item, idx) => {
                        return (
                          <Input
                            data-qa={`children-${idx}`}
                            key={idx}
                            id={idx}
                            label={`Child ${idx + 1}`}
                            placeholder={`Enter age of child ${idx + 1}`}
                            min="0"
                            type="number"
                            pattern="\d*"
                            value={socialHistory?.children.ages[idx]}
                            onChange={(e) => updateChildAge(e.target.value, idx)}
                            disabled={diffDosView}
                          />
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          </Container>
        )}

        {cnDisplaySettings && showNarrative && (
          <div className="!mt-6">
            <NarrativeForm
              syncNarrative={syncNarrative}
              setSyncNarrative={setSyncNarrative}
              onChange={(event) => handleNarrative({ event, sync: false })}
              setCurrentForm={setSocialHistory}
              restData={{
                className: 'w-full',
                label: 'Social History Narrative',
                placeholder: 'Add narrative here',
                id: 'Narrative-social-history',
                'data-qa': 'narrative-social-history',
                name: 'Narrative-social-history',
                value: socialHistory?.narrative,
                forwardedRef: textAreaRef,
                formName: 'socialHistory'
              }}
              narrativeOptions={[...narrativeOptions, ...iaRa(options)]}
            />
          </div>
        )}

        <button
          className="hidden"
          ref={hpNoteOverviewRef}
          onClick={() => {
            submitForm({
              ...customFormsParams,
              formId,
              currentFormData: socialHistory,
              defaultState: prevSocialHistory,
              type: 'socialHistory',
              user
            });
            setPrevSocialHistory(socialHistory);
          }}
          disabled={diffDosView}
        />
      </div>

      {/* <div className="my-auto ml-3">
        <Icon
          icon="trash-mono"
          onClick={() => {
            if (!formId) {
              showAlert({
                title: 'Social History',
                message: 'Please create the form before deleting one.',
                color: 'warning'
              });
              return;
            }
            mutateDeleteMedicalHistory.mutate();
          }}
        />
      </div> */}
    </div>
  );
};

export default ClinicalSocialHistory;
